import React from "react";
import { Camera, ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";
import ImageLightbox from "./ImageLightbox";
import { normalizeHistoricalGalleryItems } from "../utils/historicalGallery";

/**
 * Universal compact horizontal gallery for foto antiche / historical / archive images.
 * Always renders a scrollable strip (never a vertical grid). Clicks open ImageLightbox.
 */
export default function HistoricalArchiveGallery({
  images = [],
  title = "Foto antiche",
  backLabel = "Torna alla scheda",
  description = "",
  className = "",
  showHeader = true,
  showCount = true,
  emptyMessage = "",
  testId = "historical-archive-gallery",
  embed = false,
}) {
  const items = React.useMemo(() => normalizeHistoricalGalleryItems(images), [images]);
  const galleryUrls = React.useMemo(() => items.map((item) => item.url), [items]);
  const [active, setActive] = React.useState(0);
  const [lightboxOpen, setLightboxOpen] = React.useState(false);
  const stripRef = React.useRef(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);

  React.useEffect(() => {
    setActive(0);
    setLightboxOpen(false);
  }, [galleryUrls.length]);

  const updateScrollHints = React.useCallback(() => {
    const el = stripRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  React.useEffect(() => {
    updateScrollHints();
    const el = stripRef.current;
    if (!el) return undefined;
    el.addEventListener("scroll", updateScrollHints, { passive: true });
    window.addEventListener("resize", updateScrollHints);
    return () => {
      el.removeEventListener("scroll", updateScrollHints);
      window.removeEventListener("resize", updateScrollHints);
    };
  }, [items.length, updateScrollHints]);

  const scrollStrip = (direction) => {
    const el = stripRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * Math.max(el.clientWidth * 0.72, 180), behavior: "smooth" });
  };

  const openAt = (index) => {
    setActive(index);
    setLightboxOpen(true);
  };

  if (!items.length) {
    return emptyMessage ? (
      <div className={`rounded-xl border border-borderline bg-parchment/60 p-4 text-sm text-ink2 ${className}`}>
        {emptyMessage}
      </div>
    ) : null;
  }

  const showStripNav = items.length > 1;
  const shellClass = embed
    ? className
    : `overflow-hidden rounded-2xl border border-borderline bg-white/90 p-3 md:p-4 shadow-sm ${className}`;

  return (
    <section
      className={shellClass}
      data-testid={testId}
      aria-label={title}
    >
      {showHeader && (
        <div className="mb-3 flex flex-wrap items-end justify-between gap-3 px-0.5">
          <div className="min-w-0">
            <p className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-terracotta">
              <Camera size={13} aria-hidden />
              {title}
            </p>
            {description ? (
              <p className="mt-1 max-w-2xl text-xs leading-relaxed text-ink2">{description}</p>
            ) : (
              <p className="mt-1 text-xs text-ink2">Scorri orizzontalmente · clicca per ingrandire</p>
            )}
          </div>
          {showCount && (
            <span className="rounded-full border border-borderline bg-parchment2 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-ink2">
              {items.length} foto
            </span>
          )}
        </div>
      )}

      <div className="relative">
        {showStripNav && canScrollLeft && (
          <button
            type="button"
            onClick={() => scrollStrip(-1)}
            className="absolute left-0 top-1/2 z-10 hidden -translate-y-1/2 items-center justify-center rounded-full border border-borderline bg-white/95 p-2 text-ink shadow-md hover:border-terracotta hover:text-terracotta sm:inline-flex"
            aria-label="Scorri foto antiche indietro"
          >
            <ChevronLeft size={18} />
          </button>
        )}
        {showStripNav && canScrollRight && (
          <button
            type="button"
            onClick={() => scrollStrip(1)}
            className="absolute right-0 top-1/2 z-10 hidden -translate-y-1/2 items-center justify-center rounded-full border border-borderline bg-white/95 p-2 text-ink shadow-md hover:border-terracotta hover:text-terracotta sm:inline-flex"
            aria-label="Scorri foto antiche avanti"
          >
            <ChevronRight size={18} />
          </button>
        )}

        <div
          ref={stripRef}
          className={`historical-archive-gallery__strip flex gap-2.5 overflow-x-auto pb-1 scroll-smooth snap-x snap-mandatory ${
            showStripNav ? "sm:px-8" : ""
          }`}
          aria-label={`Carosello ${title}`}
        >
          {items.map((item, index) => (
            <figure
              key={`${item.url}-${index}`}
              className="historical-archive-gallery__tile group shrink-0 snap-start"
              data-testid={`historical-archive-item-${index}`}
            >
              <button
                type="button"
                onClick={() => openAt(index)}
                className="relative block aspect-[4/3] w-[min(42vw,11rem)] cursor-zoom-in overflow-hidden rounded-xl border border-borderline bg-parchment2 text-left shadow-sm transition hover:border-terracotta/50 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-terracotta/40 sm:w-44 md:w-48"
                aria-label={`Apri foto antica ${index + 1} di ${items.length}`}
              >
                <img
                  src={item.url}
                  alt={item.caption || `${title} ${index + 1}`}
                  className="historical-photo absolute inset-0 h-full w-full object-cover object-center transition-transform duration-300 group-hover:scale-[1.03]"
                  loading="lazy"
                  decoding="async"
                  width="320"
                  height="240"
                />
                <span className="absolute bottom-1.5 right-1.5 inline-flex items-center gap-1 rounded-full bg-ink/75 px-2 py-0.5 font-mono text-[9px] text-parchment backdrop-blur-sm">
                  <Maximize2 size={10} aria-hidden />
                  {index + 1}
                </span>
              </button>
              {item.caption ? (
                <figcaption className="mt-1.5 max-w-[min(42vw,11rem)] px-0.5 text-[11px] leading-snug text-ink2 line-clamp-2 sm:max-w-44 md:max-w-48">
                  {item.caption}
                </figcaption>
              ) : null}
            </figure>
          ))}
        </div>
      </div>

      {lightboxOpen && (
        <ImageLightbox
          images={galleryUrls}
          initialIndex={active}
          title={title}
          backLabel={backLabel}
          onClose={() => setLightboxOpen(false)}
          onIndexChange={setActive}
        />
      )}
    </section>
  );
}
